import { useEffect, useRef, useState } from "react";

interface CaptchaError extends Error {
  kind: "internal_error" | "network_error" | "token_error" | "client_error";
  statusCode?: number;
}

interface CaptchaConfiguration {
  apiKey: string;
  onSuccess: (wafToken: string) => void;
  onError?: (error: CaptchaError) => void;
  onLoad?: () => void;
  onPuzzleTimeout?: () => void;
  onPuzzleCorrect?: () => void;
  onPuzzleIncorrect?: () => void;
  defaultLocale?: string;
  disableLanguageSelector?: boolean;
  dynamicWidth?: boolean;
  skipTitle?: boolean;
}

declare global {
  interface Window {
    AwsWafCaptcha?: {
      renderCaptcha: (container: Element, configuration: CaptchaConfiguration) => void;
    };
  }
}

const WAF_CAPTCHA_API_KEY: string = import.meta.env.VITE_AWS_WAF_CAPTCHA_API_KEY || "";

// How long to wait for the deferred jsapi.js script before giving up
const SDK_LOAD_TIMEOUT_MS = 10000;
const SDK_POLL_INTERVAL_MS = 100;

interface WafCaptchaProps {
  onSuccess: (wafToken: string) => void;
}

const WafCaptcha = ({ onSuccess }: WafCaptchaProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    let waited = 0;

    const render = () => {
      if (cancelled || !containerRef.current || !window.AwsWafCaptcha) return;

      containerRef.current.innerHTML = "";
      window.AwsWafCaptcha.renderCaptcha(containerRef.current, {
        apiKey: WAF_CAPTCHA_API_KEY,
        dynamicWidth: true,
        skipTitle: true,
        onSuccess: (wafToken) => {
          if (!cancelled) onSuccess(wafToken);
        },
        onError: () => {
          if (!cancelled) {
            setError("The verification failed to load. Please try again.");
          }
        },
      });
    };

    const waitForSdk = () => {
      if (cancelled) return;

      if (window.AwsWafCaptcha) {
        render();
        return;
      }

      waited += SDK_POLL_INTERVAL_MS;
      if (waited >= SDK_LOAD_TIMEOUT_MS) {
        setError("The verification failed to load. Please refresh the page and try again.");
        return;
      }

      setTimeout(waitForSdk, SDK_POLL_INTERVAL_MS);
    };

    setError("");
    waitForSdk();

    return () => {
      cancelled = true;
    };
  }, [onSuccess, retryKey]);

  if (error) {
    return (
      <div className="flex flex-col items-center gap-4">
        <p className="text-destructive text-sm font-normal text-left">{error}</p>
        <button
          type="button"
          className="text-sm underline underline-offset-2 cursor-pointer"
          onClick={() => {
            // Clear the error in the same batch so the container div is
            // mounted before the effect calls render()
            setError("");
            setRetryKey((key) => key + 1);
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  // The WAF widget always renders dark text and has no theme support, so keep
  // it on a light surface to stay readable when the app is in dark mode
  return (
    <div className="w-full rounded-lg bg-white p-3 scheme-light">
      <div ref={containerRef} className="w-full flex justify-center" />
    </div>
  );
};

export default WafCaptcha;
